/**
 * Dynamic RBAC menu configuration.
 * Each item maps a permission slug to a menu entry. No hardcoded roles.
 * Paths use {rolePath} placeholder, replaced at runtime with /{userRole}.
 *
 * Filtering fields:
 * - permission / permissions: permission slug(s) checked against user's RBAC permissions
 * - excludeRoles: hide item for these roles even if permission matches
 * - onlyRoles: show item ONLY for these roles (if set)
 * - pathType: "student" uses /student/ paths, "role" uses {rolePath}
 */

import { EXAM_MODULE_PERMISSIONS } from "../utils/examPermissions";
import { CHAT_MENU_PERMISSIONS } from "../utils/chatPermissions";
import { LEAVE_REVIEW_MENU_PERMISSIONS } from "../utils/leavePermissions";
import { ALL_IMPORT_PERMISSION_SLUGS } from "../utils/importExportPermissions";
import { EDIT_LECTURE_MENU_PERMISSIONS } from "../utils/editLecturePermissions";

const EXCLUDE_SUPERADMIN = ["superadmin"];
const EXCLUDE_ADMIN_AND_ABOVE = ["superadmin", "admin"];
const EXCLUDE_TEACHER_AND_BELOW = ["teacher", "student"];
const ONLY_STUDENT = ["student"];
const EXCLUDE_NON_TEACHING_STAFF = ["worker"];

export const MENU_CONFIG = [
  // ──────────── Common ────────────
  {
    id: "dashboard",
    title: "Dashboard",
    icon: "Dashboard",
    path: "/dashboard",
    moduleKey: "dashboard",
    permission: "dashboard.read",
  },
  {
    id: "profile",
    title: "Profile",
    icon: "Person",
    path: "/profile",
    permission: "profile.read",
    group: "Account and Support",
  },
  {
    id: "notifications",
    title: "Notifications",
    icon: "Notifications",
    path: "/notifications",
    permission: "notification.read",
    excludeRoles: EXCLUDE_SUPERADMIN,
  },
  // ──────────── Staff common ────────────
  {
    id: "apply-leave",
    title: "Apply Leave",
    icon: "EventNote",
    path: "/apply-leave",
    permission: "leave.apply",
    excludeRoles: EXCLUDE_ADMIN_AND_ABOVE,
  },
  // ──────────── Student menu items ────────────
  {
    id: "student-assignments",
    title: "Assignments",
    icon: "Assignment",
    path: "/student/assignments",
    permission: "assignment.read",
    group: "Student - Academics",
    pathType: "student",
    onlyRoles: ONLY_STUDENT,
  },
  {
    id: "student-materials",
    title: "Materials",
    icon: "MenuBook",
    path: "/student/materials",
    permission: "material.read",
    group: "Student - Academics",
    pathType: "student",
    onlyRoles: ONLY_STUDENT,
  },
  {
    id: "student-timetable",
    title: "Timetable",
    icon: "CalendarMonth",
    path: "/student/timetable",
    permission: "timetable.read",
    group: "Student - Academics",
    pathType: "student",
    onlyRoles: ONLY_STUDENT,
  },
  {
    id: "student-exam-results",
    title: "Exam Results",
    icon: "Grade",
    path: "/student/exam-results",
    permission: "exam.result.view",
    group: "Student - Academics",
    pathType: "student",
    onlyRoles: ONLY_STUDENT,
  },
  {
    id: "student-attendance",
    title: "My Attendance",
    icon: "EventAvailable",
    path: "/student/attendance",
    permission: "attendance.read",
    group: "Student - Attendance & Leaves",
    pathType: "student",
    onlyRoles: ONLY_STUDENT,
  },
  {
    id: "student-leaves",
    title: "Leave Applications",
    icon: "AccessTime",
    path: "/student/leaves",
    permission: "leave.apply",
    group: "Student - Attendance & Leaves",
    pathType: "student",
    onlyRoles: ONLY_STUDENT,
  },
  {
    id: "student-reports",
    title: "Reports",
    icon: "Assessment",
    path: "/student/reports",
    permission: "report.read",
    pathType: "student",
    onlyRoles: ONLY_STUDENT,
  },
  {
    id: "student-fees",
    title: "My Fees",
    icon: "Payments",
    path: "/student/fees",
    permission: "fees.pay",
    group: "Student - Academics",
    pathType: "student",
    onlyRoles: ONLY_STUDENT,
  },
  // ──────────── Teacher/Staff menu items ────────────
  {
    id: "students",
    title: "Student",
    icon: "People",
    path: "{rolePath}/students",
    permission: "student.profile.read",
    group: "Department",
    pathType: "role",
    excludeRoles: [...EXCLUDE_SUPERADMIN, ...EXCLUDE_NON_TEACHING_STAFF],
  },
  {
    id: "smart-attendance",
    title: "Smart Attendance",
    icon: "EventAvailable",
    path: "{rolePath}/smart-attendance",
    moduleKey: "attendance",
    // QR/OTP session: start or mark; mark_manual also grants access (common institute matrix)
    // Also allow read-only visibility when user has attendance.read (menu visibility only; APIs still enforce actions).
    permissions: ["attendance.smart", "attendance.read", "attendance.start", "attendance.mark", "attendance.mark_manual"],
    group: "Attendance",
    pathType: "role",
    excludeRoles: [...EXCLUDE_SUPERADMIN, ...EXCLUDE_NON_TEACHING_STAFF],
  },
  {
    id: "manual-attendance-upload",
    title: "Manual Attendance Upload",
    icon: "CloudUpload",
    path: "{rolePath}/attendance/manual-upload",
    moduleKey: "attendance",
    permissions: ["attendance.manual_upload", "attendance.read", "attendance.mark_manual", "attendance.start"],
    group: "Attendance",
    pathType: "role",
    excludeRoles: [...EXCLUDE_SUPERADMIN, ...EXCLUDE_NON_TEACHING_STAFF],
  },
  {
    id: "special-sessions-attendance",
    title: "Special Sessions",
    icon: "EventNote",
    path: "{rolePath}/special-sessions",
    moduleKey: "attendance",
    permissions: ["special_session.read", "special_session.manage", "attendance.read", "attendance.start", "attendance.mark", "attendance.mark_manual"],
    group: "Attendance",
    pathType: "role",
    excludeRoles: [...EXCLUDE_SUPERADMIN, ...EXCLUDE_NON_TEACHING_STAFF],
  },
  {
    id: "timetable",
    title: "My Timetable",
    icon: "CalendarMonth",
    path: "{rolePath}/my-timetable",
    permission: "timetable.read",
    group: "Schedule & Timetable",
    pathType: "role",
    requirePermission: "attendance.mark",
    // Only show personal timetable for teacher/HOD-style roles.
    onlyRoles: ["teacher", "hod"],
    excludeRoles: EXCLUDE_SUPERADMIN,
  },
  {
    id: "timetable-dashboard",
    title: "Timetable Dashboard",
    icon: "CalendarMonth",
    path: "{rolePath}/timetable",
    permission: "timetable.read",
    moduleKey: "timetable",
    group: "Schedule & Timetable",
    pathType: "role",
    excludeRoles: EXCLUDE_SUPERADMIN,
  },
  {
    id: "timetable-generate",
    title: "Generate Timetable",
    icon: "AutoAwesome",
    path: "{rolePath}/timetable/generate",
    permission: "timetable.create",
    moduleKey: "timetable",
    group: "Schedule & Timetable",
    pathType: "role",
    excludeRoles: EXCLUDE_SUPERADMIN,
  },
  {
    id: "schedule-change",
    title: "Schedule Change",
    icon: "AccessTime",
    path: "{rolePath}/schedule-change",
    permission: "schedule.request_change",
    group: "Schedule & Timetable",
    pathType: "role",
    // Teachers/HOD/staff request changes; institute owner uses timetable admin flows instead.
    excludeRoles: [...EXCLUDE_SUPERADMIN, "institute"],
  },
  {
    id: "edit-lecture",
    title: "Edit Lecture",
    icon: "EventNote",
    path: "{rolePath}/edit-lecture/department-schedule",
    permissions: EDIT_LECTURE_MENU_PERMISSIONS,
    moduleKey: "edit_lecture",
    group: "Schedule & Timetable",
    pathType: "role",
  },
  {
    id: "assignments",
    title: "Assignments",
    icon: "Assignment",
    path: "{rolePath}/assignments",
    permission: "assignment.read",
    group: "Academics",
    pathType: "role",
    excludeRoles: EXCLUDE_SUPERADMIN,
  },
  {
    id: "assignment-approval",
    title: "Grade Submissions",
    icon: "Assignment",
    path: "{rolePath}/assignment-approval",
    permission: "assignment.approve",
    group: "Academics",
    pathType: "role",
    excludeRoles: EXCLUDE_SUPERADMIN,
  },
  {
    id: "materials",
    title: "Materials",
    icon: "MenuBook",
    path: "{rolePath}/materials",
    permission: "material.read",
    group: "Academics",
    pathType: "role",
    excludeRoles: EXCLUDE_SUPERADMIN,
  },
  {
    id: "documents",
    title: "Documents",
    icon: "Description",
    path: "{rolePath}/documents",
    permission: "documents.view",
    moduleKey: "documents",
    group: "Department",
    pathType: "role",
    excludeRoles: EXCLUDE_SUPERADMIN,
  },
  {
    id: "exams",
    title: "Exams",
    icon: "Grade",
    path: "{rolePath}/exams",
    permissions: EXAM_MODULE_PERMISSIONS,
    group: "Academics",
    pathType: "role",
    excludeRoles: EXCLUDE_SUPERADMIN,
  },
  {
    id: "exam-management",
    title: "Exam Management",
    icon: "Grade",
    path: "{rolePath}/exam-management",
    permissions: EXAM_MODULE_PERMISSIONS,
    group: "Academics",
    pathType: "role",
    excludeRoles: EXCLUDE_SUPERADMIN,
  },
  {
    id: "class-notifications",
    title: "Class Notifications",
    icon: "Campaign",
    path: "{rolePath}/class-notifications",
    permission: "notification.create_class",
    group: "Department",
    pathType: "role",
    excludeRoles: EXCLUDE_SUPERADMIN,
  },
  {
    id: "leaves",
    title: "Leave Requests",
    icon: "AccessTime",
    path: "{rolePath}/leaves",
    permissions: LEAVE_REVIEW_MENU_PERMISSIONS,
    moduleKey: "leave",
    group: "Leave and Request",
    pathType: "role",
    excludeRoles: EXCLUDE_SUPERADMIN,
  },
  {
    id: "requests",
    title: "Profile Edit Requests",
    icon: "Person",
    path: "{rolePath}/requests",
    permissions: ["student.profile.update", "teacher.profile.update"],
    group: "Leave and Request",
    pathType: "role",
    excludeRoles: EXCLUDE_SUPERADMIN,
  },
  {
    id: "device-login-requests",
    title: "Device Login Approvals",
    icon: "Security",
    path: "{rolePath}/requests/device-logins",
    permissions: [
      "device.student.read",
      "device.student.approve",
      "device.student.reject",
      "device.staff.read",
      "device.staff.approve",
      "device.staff.reject",
      "student.profile.update",
      "teacher.profile.update",
    ],
    group: "Leave and Request",
    pathType: "role",
    excludeRoles: EXCLUDE_SUPERADMIN,
  },
  
  {
    id: "reports",
    title: "Reports & Analytics",
    icon: "Assessment",
    path: "{rolePath}/reports",
    permission: "report.read",
    group: "Academics",
    pathType: "role",
    excludeRoles: EXCLUDE_SUPERADMIN,
  },
  // ──────────── HOD / Department ────────────
  {
    id: "teachers",
    title: "Teachers",
    icon: "People",
    path: "{rolePath}/teachers",
    permission: "teacher.profile.read",
    group: "Department",
    pathType: "role",
    excludeRoles: EXCLUDE_SUPERADMIN,
  },
  {
    id: "courses",
    title: "Courses",
    icon: "Class",
    path: "{rolePath}/courses",
    permission: "course.read",
    group: "Department",
    pathType: "role",
    excludeRoles: EXCLUDE_SUPERADMIN,
  },
  {
    id: "classes",
    title: "Classes",
    icon: "Class",
    path: "{rolePath}/classes",
    permission: "class.read",
    moduleKey: "class",
    group: "Department",
    pathType: "role",
    excludeRoles: EXCLUDE_SUPERADMIN,
  },
  {
    id: "subjects",
    title: "Subjects",
    icon: "Subject",
    path: "{rolePath}/subjects",
    permission: "subject.read",
    moduleKey: "subject",
    group: "Department",
    pathType: "role",
    excludeRoles: EXCLUDE_SUPERADMIN,
  },
  // ──────────── Admin / Institute ────────────
  {
    id: "users",
    title: "Users",
    icon: "People",
    path: "{rolePath}/users",
    permission: "user.read",
    moduleKey: "user",
    group: "Administration",
    pathType: "role",
    excludeRoles: EXCLUDE_SUPERADMIN,
  },
  {
    id: "departments",
    title: "Departments",
    icon: "Business",
    path: "{rolePath}/departments",
    permission: "department.read",
    moduleKey: "department",
    group: "Administration",
    pathType: "role",
    excludeRoles: EXCLUDE_SUPERADMIN,
  },
  {
    id: "leave-management",
    title: "Leave Management",
    icon: "EventNote",
    path: "{rolePath}/leave-management",
    permissions: ["leave.read", "leave.update", "leave.create"],
    moduleKey: "leave",
    group: "Administration",
    pathType: "role",
    excludeRoles: EXCLUDE_SUPERADMIN,
  },
  // Student promotion is accessed from the Students page (no separate menu item).
  {
    id: "import",
    title: "Import/Export",
    icon: "CloudUpload",
    path: "{rolePath}/import",
    permissions: ALL_IMPORT_PERMISSION_SLUGS,
    group: "Administration",
    pathType: "role",
    excludeRoles: EXCLUDE_SUPERADMIN,
  },
  {
    id: "audit-logs-admin",
    title: "Audit Logs",
    icon: "Assessment",
    path: "{rolePath}/audit-logs",
    // Same pattern as super admin: focused permission check (audit.read for institute)
    permission: "audit.read",
    group: "Administration",
    pathType: "role",
    // Show for admin (institute) and hod; exclude superadmin (has own), teacher, student
    excludeRoles: ["superadmin", "teacher", "student"],
  },
  {
    id: "m9t7gv",
    title: "RBAC",
    icon: "Security",
    group: "Administration",
    moduleKey: "rbac",
    // Permission-driven (any custom role with RBAC grants sees this menu).
    requireAny: ["rbac.manage", "role.assign"],
    submenu: [
      {
        id: "institute-rbac-roles-modules",
        title: "Roles & Modules",
        icon: "Security",
        path: "{rolePath}/rbac",
        permissions: ["rbac.manage", "role.assign"],
        pathType: "role",
      },
      {
        id: "institute-rbac-role-permissions",
        title: "Role Permissions",
        icon: "Security",
        path: "{rolePath}/roles-permissions",
        permissions: ["rbac.manage", "role.assign"],
        pathType: "role",
      },
    ],
  },
  {
    id: "help-faqs",
    title: "Help and Support",
    icon: "HelpOutline",
    path: "/help-faqs",
    permissions: ["faq.read", "faq.create"],
    moduleKey: "faq",
    // Visible to any role with FAQ permissions (teacher/hod/institute/admin) when module is enabled
    group: "Account and Support",
    excludeRoles: EXCLUDE_SUPERADMIN,
  },
  // ──────────── Super Admin ────────────
  {
    id: "institutes",
    title: "Institutes",
    icon: "School",
    path: "{rolePath}/institutes",
    permission: "institute.read",
    group: "Super Admin",
    pathType: "role",
    onlyRoles: ["superadmin"],
  },
  {
    id: "all-super-admins",
    title: "Admin Users",
    icon: "People",
    path: "{rolePath}/all-super-admins",
    permission: "admin.read",
    group: "Super Admin",
    pathType: "role",
    onlyRoles: ["superadmin"],
  },
  {
    id: "all-users",
    title: "All Users",
    icon: "People",
    path: "{rolePath}/all-users",
    // permission: "user.read",
    group: "Super Admin",
    pathType: "role",
    onlyRoles: ["superadmin"],
  },
  {
    id: "import-all",
    title: "Import Data",
    icon: "CloudUpload",
    path: "{rolePath}/import-all",
    permission: "superadmin.import",
    group: "Super Admin",
    pathType: "role",
    onlyRoles: ["superadmin"],
  },
  {
    id: "rbac",
    title: "RBAC Management",
    icon: "Security",
    path: "{rolePath}/rbac",
    permission: "rbac.manage",
    group: "Super Admin",
    pathType: "role",
    onlyRoles: ["superadmin"],
  },
  {
    id: "roles-permissions",
    title: "Roles & Permissions",
    icon: "Security",
    path: "{rolePath}/roles-permissions",
    permission: "permission.manage",
    group: "Super Admin",
    pathType: "role",
    onlyRoles: ["superadmin"],
  },
  {
    id: "superadmin-global-notifications",
    title: "Global Notifications",
    icon: "Notifications",
    path: "/superadmin/global-notifications",
    group: "Super Admin",
    pathType: "role",
    onlyRoles: ["superadmin"],
  },
  {
    id: "superadmin-broadcast",
    title: "Broadcast & Groups",
    icon: "Groups",
    path: "/superadmin/broadcast",
    group: "Super Admin",
    pathType: "role",
    onlyRoles: ["superadmin"],
  },
  {
    id: "audit-logs-superadmin",
    title: "Audit Logs",
    icon: "Assessment",
    path: "{rolePath}/audit-logs",
    permissions: ["rbac.manage", "permission.manage"],
    group: "Super Admin",
    pathType: "role",
    onlyRoles: ["superadmin"],
  },
  {
    id: "consent-forms",
    title: "Consent Forms",
    icon: "Security",
    path: "{rolePath}/consent-forms",
    permission: "permission.manage",
    group: "Super Admin",
    pathType: "role",
    onlyRoles: ["superadmin"],
  },
  {
    id: "support-inbox",
    title: "Support messages",
    icon: "Support",
    path: "/chat",
    group: "Super Admin",
    pathType: "role",
    onlyRoles: ["superadmin"],
  },
  // ──────────── Chat ────────────
  {
    id: "chat",
    title: "Messages",
    icon: "Chat",
    path: "/chat",
    permissions: CHAT_MENU_PERMISSIONS,
    excludeRoles: EXCLUDE_SUPERADMIN,
  },
  // ──────────── Fees Management ────────────
  {
    id: "fee-heads",
    title: "Fee Heads",
    icon: "ListAlt",
    path: "{rolePath}/fees/heads",
    permission: "fees.manage",
    group: "Fees Management",
    pathType: "role",
    excludeRoles: EXCLUDE_SUPERADMIN,
  },
  {
    id: "fee-structure",
    title: "Fee Structure",
    icon: "AccountBalance",
    path: "{rolePath}/fees/structures",
    permission: "fees.manage",
    group: "Fees Management",
    pathType: "role",
    excludeRoles: EXCLUDE_SUPERADMIN,
  },
  {
    id: "student-fees-admin",
    title: "Student Fees",
    icon: "People",
    path: "{rolePath}/fees/student-fees",
    permissions: ["fees.read", "fees.manage"],
    group: "Fees Management",
    pathType: "role",
    excludeRoles: EXCLUDE_SUPERADMIN,
  },
  {
    id: "fee-collection",
    title: "Fee Collection",
    icon: "Payments",
    path: "{rolePath}/fees/collection",
    permission: "fees.manage",
    group: "Fees Management",
    pathType: "role",
    excludeRoles: EXCLUDE_SUPERADMIN,
  },
  {
    id: "fee-reports",
    title: "Fee Reports",
    icon: "Assessment",
    path: "{rolePath}/fees/reports",
    permissions: ["fees.reports", "fees.manage"],
    group: "Fees Management",
    pathType: "role",
    excludeRoles: EXCLUDE_SUPERADMIN,
  },
  {
    id: "fee-settings",
    title: "Fee Settings",
    icon: "Tune",
    path: "{rolePath}/fees/settings",
    permission: "fees.manage",
    group: "Fees Management",
    pathType: "role",
    excludeRoles: EXCLUDE_SUPERADMIN,
  },
  {
    id: "payment-setup",
    title: "Payment Setup",
    icon: "Settings",
    path: "{rolePath}/settings/payment-setup",
    permissions: ["fees.payment_setup", "fees.manage"],
    group: "Settings",
    pathType: "role",
    excludeRoles: EXCLUDE_SUPERADMIN,
  },
  {
    id: "registration-settings",
    title: "Registration Settings",
    icon: "Tune",
    path: "{rolePath}/registration-settings",
    permissions: ["registration.prn.manage", "registration.manage"],
    group: "Settings",
    pathType: "role",
    excludeRoles: EXCLUDE_SUPERADMIN,
  },
  {
    id: "approve-registration",
    title: "Approve Registration",
    icon: "ListAlt",
    path: "{rolePath}/approve-registration",
    permission: "registration.menu_view",
    group: "Settings",
    pathType: "role",
    excludeRoles: EXCLUDE_SUPERADMIN,
  },
  // ──────────── Guardian Faculty Member (GFM) ────────────
  {
    id: "gfm-defaulters",
    title: "Defaulter Dashboard",
    icon: "Assessment",
    path: "{rolePath}/gfm/defaulters",
    permissions: ["gfm.mentees.view", "gfm.view_all"],
    moduleKey: "gfm",
    group: "Guardian Faculty",
    pathType: "role",
    excludeRoles: EXCLUDE_SUPERADMIN,
  },
  {
    id: "gfm-assignments",
    title: "Guardian Faculty Assignments",
    icon: "SupervisorAccount",
    path: "{rolePath}/gfm/assignments",
    permission: "gfm.manage",
    moduleKey: "gfm",
    group: "Guardian Faculty",
    pathType: "role",
    excludeRoles: EXCLUDE_SUPERADMIN,
  },
  {
    id: "gfm-settings",
    title: "Guardian Faculty Settings",
    icon: "SupervisorAccount",
    path: "{rolePath}/gfm/settings",
    permission: "gfm.manage",
    moduleKey: "gfm",
    group: "Guardian Faculty",
    pathType: "role",
    excludeRoles: EXCLUDE_SUPERADMIN,
  },
  // ──────────── Organization / Academic ────────────
  {
    id: "organization",
    title: "Organization",
    icon: "Business",
    path: "/organization",
    permission: "organization.read",
    excludeRoles: EXCLUDE_SUPERADMIN,
  },
  {
    id: "academic-info",
    title: "Academic Info",
    icon: "School",
    path: "/academic-info",
    permission: "academic.read",
    group: "Academics",
    excludeRoles: EXCLUDE_SUPERADMIN,
  },
  {
    id: "department-info",
    title: "Department Info",
    icon: "Business",
    path: "/department",
    permissions: ["department.info", "department.read"],
    group: "Department",
    excludeRoles: EXCLUDE_SUPERADMIN,
  },
  {
    id: "contact-requests",
    title: "Contact Info Requests",
    icon: "Settings",
    path: "/profile/contact-requests",
    permission: "contact.request_edit",
    excludeRoles: EXCLUDE_ADMIN_AND_ABOVE,
    group: "Leave and Request",
  },
  {
    id: "contact-support",
    title: "Contact support",
    icon: "Support",
    path: "/contact-support",
    permission: "support.email",
    excludeRoles: EXCLUDE_SUPERADMIN,
    group: "Account and Support",
  },
];